import { useState, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useLocation } from 'wouter';
import {
  useCreateProject,
  useGetSignalAvailableRepos,
  useGetSignalRepoPreview,
  customInstance,
} from '@edison/api-client-react';
import type {
  GetSignalAvailableRepos200DataItemsItem,
  GetSignalRepoPreview200Data,
} from '@edison/api-client-react';
import { Button } from '@/components/ui/button.js';
import { Search, GitBranch, X, Clock, Users, AlertCircle } from 'lucide-react';
import { formatFy } from '@/lib/utils.js';

// ---------------------------------------------------------------------------
// Australian financial year helpers (1 Jul – 30 Jun)
// FY2025 = 1 Jul 2024 – 30 Jun 2025
// ---------------------------------------------------------------------------

function currentFy(): string {
  const now = new Date();
  const year = now.getFullYear();
  return now.getMonth() >= 6 ? `FY${year}` : `FY${year - 1}`;
}

function fyOptions(): string[] {
  const fy = currentFy();
  const base = parseInt(fy.replace('FY', ''), 10);
  return Array.from({ length: 5 }, (_, i) => `FY${base - 3 + i}`);
}

function fyDates(fy: string): { startDate: string; endDate: string } {
  const year = parseInt(fy.replace('FY', ''), 10);
  return { startDate: `${year - 1}-07-01`, endDate: `${year}-06-30` };
}

// ---------------------------------------------------------------------------
// Relative time helper
// ---------------------------------------------------------------------------

function relativeTime(isoString: string): string {
  const diffMs = Date.now() - new Date(isoString).getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return 'today';
  if (diffDays === 1) return 'yesterday';
  if (diffDays < 30) return `${diffDays}d ago`;
  const diffMonths = Math.floor(diffDays / 30);
  if (diffMonths < 12) return `${diffMonths}mo ago`;
  return `${Math.floor(diffMonths / 12)}yr ago`;
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface FormValues {
  name: string;
  description: string;
  incomeYear: string;
}

interface SelectedRepo {
  owner: string;
  name: string;
  fullName: string;
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function RepoPreviewPanel({ owner, repo }: { owner: string; repo: string }) {
  const { data, isLoading, isError } = useGetSignalRepoPreview({ owner, repo });

  if (isLoading) {
    return (
      <div className="mt-3 rounded-lg border p-3 space-y-2">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="h-4 bg-muted rounded animate-pulse" />
        ))}
      </div>
    );
  }

  if (isError) {
    return (
      <div className="mt-3 rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive flex items-center gap-2">
        <AlertCircle className="h-4 w-4 shrink-0" />
        Could not load preview
      </div>
    );
  }

  const preview: GetSignalRepoPreview200Data | undefined = data?.data;

  if (!preview || (preview.commitCount === 0 && preview.recentCommits.length === 0)) {
    return (
      <div className="mt-3 rounded-lg border p-3 text-sm text-muted-foreground">
        No commits captured yet — backfill will run after project is created.
      </div>
    );
  }

  return (
    <div className="mt-3 rounded-lg border p-3 space-y-3">
      <div className="flex gap-4 text-sm">
        <span className="flex items-center gap-1 text-muted-foreground">
          <GitBranch className="h-3.5 w-3.5" aria-hidden="true" />
          {preview.commitCount.toLocaleString()} commits
        </span>
        <span className="flex items-center gap-1 text-muted-foreground">
          <Users className="h-3.5 w-3.5" aria-hidden="true" />
          {preview.contributorCount} contributors
        </span>
        {preview.lastCommitAt && (
          <span className="flex items-center gap-1 text-muted-foreground">
            <Clock className="h-3.5 w-3.5" aria-hidden="true" />
            Last: {relativeTime(preview.lastCommitAt)}
          </span>
        )}
      </div>
      {preview.recentCommits.length > 0 && (
        <div className="space-y-1">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
            Recent commits
          </p>
          {preview.recentCommits.slice(0, 5).map((commit) => (
            <div key={commit.id} className="flex gap-2 text-xs">
              <span className="shrink-0 font-mono text-muted-foreground/70">
                {commit.sha.slice(0, 7)}
              </span>
              <span className="truncate text-foreground/80">{commit.message}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function ProjectCreatePage() {
  const [, navigate] = useLocation();
  const { mutate: createProject, isPending: isCreating } = useCreateProject();

  const [selectedRepos, setSelectedRepos] = useState<SelectedRepo[]>([]);
  const [repoSearch, setRepoSearch] = useState('');
  const [submitError, setSubmitError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<FormValues>({
    defaultValues: { incomeYear: '' },
  });

  const selectedFy = watch('incomeYear');
  const dates = selectedFy?.match(/^FY\d{4}$/) ? fyDates(selectedFy) : null;

  // Repo list
  const {
    data: reposData,
    isLoading: reposLoading,
    isError: reposError,
  } = useGetSignalAvailableRepos();

  const allRepos: GetSignalAvailableRepos200DataItemsItem[] =
    reposData?.data?.items ?? [];

  const filteredRepos = useMemo(() => {
    const q = repoSearch.trim().toLowerCase();
    if (!q) return allRepos;
    return allRepos.filter((r) => r.full_name.toLowerCase().includes(q));
  }, [allRepos, repoSearch]);

  // Single-repo preview: only show when exactly one repo selected
  const singlePreviewRepo =
    selectedRepos.length === 1 ? selectedRepos[0] : null;

  function toggleRepo(repo: GetSignalAvailableRepos200DataItemsItem) {
    const parts = repo.full_name.split('/');
    const owner = parts[0] ?? '';
    const name = parts[1] ?? '';
    const key = repo.full_name;
    const alreadySelected = selectedRepos.some((r) => r.fullName === key);
    if (alreadySelected) {
      setSelectedRepos((prev) => prev.filter((r) => r.fullName !== key));
    } else {
      setSelectedRepos((prev) => [...prev, { owner, name, fullName: key }]);
    }
  }

  function removeRepo(fullName: string) {
    setSelectedRepos((prev) => prev.filter((r) => r.fullName !== fullName));
  }

  const onSubmit = async (values: FormValues) => {
    setSubmitError(null);

    createProject(
      {
        name: values.name,
        incomeYear: values.incomeYear, // ERA-024: required — a project with no FY is orphaned from every claim
        startDate: dates?.startDate ?? new Date().toISOString().slice(0, 10),
        ...(dates?.endDate ? { endDate: dates.endDate } : {}),
        ...(values.description ? { description: values.description } : {}),
      },
      {
        onSuccess: async (res) => {
          const project = res?.data;
          const projectId = project?.id;
          if (!projectId) {
            navigate('/projects');
            return;
          }

          // Link each selected repo — collect failures
          const linkFailures: string[] = [];
          for (const repo of selectedRepos) {
            try {
              await customInstance({
                url: `/api/projects/${projectId}/repos`,
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                data: { repoOwner: repo.owner, repoName: repo.name },
              });
            } catch {
              linkFailures.push(repo.fullName);
            }
          }

          // Surface partial failures — don't navigate so the user can see the error
          if (linkFailures.length > 0) {
            setSubmitError(
              `Project created, but ${linkFailures.length} repo(s) failed to link: ${linkFailures.join(', ')}. Navigate to the project's Signals tab to connect them.`,
            );
            return;
          }

          // Fire-and-forget backfill
          if (selectedRepos.length > 0) {
            customInstance({
              url: `/api/projects/${projectId}/backfill-timeentries`,
              method: 'POST',
            }).catch(() => {
              // Intentionally fire-and-forget
            });
          }

          navigate(`/projects/${projectId}`);
        },
        onError: () => {
          setSubmitError('Failed to create project. Please try again.');
        },
      }
    );
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div className="max-w-5xl">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold tracking-tight">New project</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Create an R&D project to begin the TFA process
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* ----------------------------------------------------------------
            Left column — project details
          ---------------------------------------------------------------- */}
          <div className="space-y-4">
            <h2 className="text-base font-medium">Project details</h2>

            <div>
              <label htmlFor="name" className="block text-sm font-medium mb-1">
                Project name <span className="text-destructive">*</span>
              </label>
              <input
                id="name"
                type="text"
                {...register('name', {
                  required: 'Project name is required',
                  maxLength: { value: 255, message: 'Max 255 characters' },
                })}
                className="w-full rounded-lg border bg-background px-3 py-2 text-sm"
                placeholder="e.g. Predictive Analytics Engine"
              />
              {errors.name && (
                <p className="mt-1 text-xs text-destructive">{errors.name.message}</p>
              )}
            </div>

            <div>
              <label htmlFor="incomeYear" className="block text-sm font-medium mb-1">
                Financial year <span className="text-destructive">*</span>
              </label>
              <select
                id="incomeYear"
                {...register('incomeYear', { required: 'Select the financial year this project is claimed in' })}
                className="w-full rounded-lg border bg-background px-3 py-2 text-sm"
              >
                <option value="">Select a financial year…</option>
                {fyOptions().map((fy) => (
                  <option key={fy} value={fy}>
                    {formatFy(fy)}
                  </option>
                ))}
              </select>
              {errors.incomeYear && (
                <p className="mt-1 text-xs text-destructive">{errors.incomeYear.message}</p>
              )}
              {dates && (
                <p className="mt-1 text-xs text-muted-foreground">
                  {dates.startDate} → {dates.endDate}
                </p>
              )}
            </div>

            <div>
              <label htmlFor="description" className="block text-sm font-medium mb-1">
                Description
              </label>
              <textarea
                id="description"
                rows={4}
                {...register('description')}
                className="w-full rounded-lg border bg-background px-3 py-2 text-sm resize-y"
                placeholder="Brief description of the R&D project"
              />
            </div>
          </div>

          {/* ----------------------------------------------------------------
            Right column — connected repos
          ---------------------------------------------------------------- */}
          <div className="space-y-3">
            <h2 className="text-base font-medium">Connected repos</h2>

            {/* Search */}
            <div className="relative">
              <Search
                className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground"
                aria-hidden="true"
              />
              <input
                type="search"
                value={repoSearch}
                onChange={(e) => setRepoSearch(e.target.value)}
                className="w-full rounded-lg border bg-background pl-9 pr-3 py-2 text-sm"
                placeholder="Search repos…"
                aria-label="Search available repos"
              />
            </div>

            {/* Repo list */}
            <div className="rounded-lg border overflow-y-auto max-h-56">
              {reposLoading && (
                <div className="px-4 py-6 text-sm text-muted-foreground text-center">
                  Loading repos…
                </div>
              )}
              {!reposLoading && reposError && (
                <div className="px-4 py-6 text-sm text-destructive flex items-center justify-center gap-2">
                  <AlertCircle className="h-4 w-4 shrink-0" aria-hidden="true" />
                  Could not load repos. GitHub App may not be configured.
                </div>
              )}
              {!reposLoading && !reposError && filteredRepos.length === 0 && (
                <div className="px-4 py-6 text-sm text-muted-foreground text-center">
                  {allRepos.length === 0
                    ? 'No repos available. Install the GitHub App first.'
                    : 'No repos match your search.'}
                </div>
              )}
              {!reposLoading &&
                !reposError &&
                filteredRepos.map((repo) => {
                  const isSelected = selectedRepos.some(
                    (r) => r.fullName === repo.full_name
                  );
                  return (
                    <button
                      key={repo.full_name}
                      type="button"
                      onClick={() => toggleRepo(repo)}
                      className={`w-full flex items-start justify-between px-4 py-3 text-left text-sm hover:bg-muted/50 transition-colors border-b last:border-b-0 ${
                        isSelected ? 'bg-primary/5' : ''
                      }`}
                      aria-pressed={isSelected}
                    >
                      <span className="flex items-center gap-2 min-w-0">
                        <GitBranch
                          className={`h-4 w-4 shrink-0 ${
                            isSelected ? 'text-primary' : 'text-muted-foreground'
                          }`}
                          aria-hidden="true"
                        />
                        <span className="flex flex-col min-w-0">
                          <span className="font-medium truncate">{repo.full_name}</span>
                          {repo.alreadyLinkedTo && (
                            <span className="text-xs text-amber-600 truncate">
                              Already linked to another project
                            </span>
                          )}
                        </span>
                      </span>
                      <span className="shrink-0 text-xs text-muted-foreground ml-2 mt-0.5">
                        {relativeTime(repo.pushed_at)}
                      </span>
                    </button>
                  );
                })}
            </div>

            {/* Selected repo tags */}
            {selectedRepos.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {selectedRepos.map((repo) => (
                  <span
                    key={repo.fullName}
                    className="inline-flex items-center gap-1 rounded-full bg-primary/10 text-primary px-2.5 py-1 text-xs font-medium"
                  >
                    {repo.fullName}
                    <button
                      type="button"
                      onClick={() => removeRepo(repo.fullName)}
                      className="hover:text-primary/70 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary rounded-full"
                      aria-label={`Remove ${repo.fullName}`}
                    >
                      <X className="h-3 w-3" aria-hidden="true" />
                    </button>
                  </span>
                ))}
              </div>
            )}

            {/* Preview panel */}
            {singlePreviewRepo && (
              <RepoPreviewPanel
                owner={singlePreviewRepo.owner}
                repo={singlePreviewRepo.name}
              />
            )}
            {selectedRepos.length > 1 && (
              <div className="rounded-lg border p-3 text-sm text-muted-foreground">
                {selectedRepos.length} repos selected — all will be linked and backfilled.
              </div>
            )}
            {selectedRepos.length === 0 && !reposLoading && !reposError && (
              <p className="text-sm text-muted-foreground">
                Select a repo to preview its activity
              </p>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="mt-8 flex items-center gap-3 border-t pt-6">
          {submitError && (
            <p className="text-sm text-destructive flex items-center gap-1.5 mr-auto">
              <AlertCircle className="h-4 w-4 shrink-0" aria-hidden="true" />
              {submitError}
            </p>
          )}
          <div className="flex gap-3 ml-auto">
            <Button
              type="button"
              variant="ghost"
              onClick={() => navigate('/projects')}
            >
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={isCreating}>
              {isCreating ? 'Creating…' : 'Create + backfill'}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
}
